import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, LogOut, CalendarFold, NotebookTabs, Inbox, Users, Clock } from "lucide-react";
import Sidebar from "./Sidebar";

const VisitDetails = () => {
  const { visitId } = useParams();
  const navigate = useNavigate();
  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const [showModal, setShowModal] = useState(false);
const [modalMessage, setModalMessage] = useState("");
const [modalType, setModalType] = useState("success"); 

const [isApproving, setIsApproving] = useState(false);
const [isRejecting, setIsRejecting] = useState(false); 

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    setUserData(user);
    setIsAdmin(user?.role === "Admin");
  }, []);

  useEffect(() => {
    const fetchVisitDetails = async () => {
      try {
        setLoading(true);
        const response = await fetch("https://njs-01.optimuslab.space/api/visits/logbook");
        const data = await response.json();
        const foundVisit = data.logbook.find(v => v._id === visitId);
        
        if (foundVisit) {
          setVisit(foundVisit);
        }
      } catch (error) {
        console.error("Error fetching visit details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchVisitDetails();
  }, [visitId]);

  const handleApprove = async () => {
    setIsApproving(true); 
    try {
      const response = await fetch(
        `https://njs-01.optimuslab.space/api/visits/${visitId}/approve`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            approvedBy: userData?.name || "Admin User"
          })
        }
      );
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const result = await response.json();
      
      setVisit({
        ...visit,
        approvalStatus: "approved",
        approvedBy: userData?.name || "Admin User",
        approvedAt: new Date().toISOString()
      });
      
      setModalType("success");
      setModalMessage(result.message || "Visit approved successfully!");
      setShowModal(true);
      
      // Auto close after 3 seconds
      setTimeout(() => {
        setShowModal(false);
      }, 3000);
      
    } catch (error) {
      console.error("Error approving visit:", error);
      setModalType("error");
      setModalMessage("Failed to approve visit. Please try again.");
      setShowModal(true);
      
      setTimeout(() => {
        setShowModal(false);
      }, 3000);
    }
    finally {
        setIsApproving(false); 
      }
  };

  const handleReject = async () => {
    setIsRejecting(true);
    try {
      const response = await fetch(
        `https://njs-01.optimuslab.space/api/visits/${visitId}/reject`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            rejectedBy: userData?.name || "Admin User"
          })
        }
      );
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const result = await response.json();
      
      setVisit({
        ...visit,
        approvalStatus: "rejected",
        rejectedBy: userData?.name || "Admin User",
        rejectedAt: new Date().toISOString()
      });
      
      setModalType("success");
      setModalMessage(result.message || "Visit rejected successfully!");
      setShowModal(true);
      
      setTimeout(() => {
        setShowModal(false);
      }, 3000);
      
    } catch (error) {
      console.error("Error rejecting visit:", error);
      setModalType("error");
      setModalMessage("Failed to reject visit. Please try again.");
      setShowModal(true);
      
      setTimeout(() => {
        setShowModal(false);
      }, 3000);
    } finally {
      setIsRejecting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("access_token");
    navigate("/");
  };

  const formatToLocalTime = (utcTimestamp) => {
    if (!utcTimestamp) return "-";
    const date = new Date(utcTimestamp);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatExpectedTime = (utcTimestamp) => {
    if (!utcTimestamp) return "-";
    const date = new Date(utcTimestamp);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatToHKT = (utcTimestamp) => {
    if (!utcTimestamp) return "-";
    const date = new Date(utcTimestamp);
    return date.toLocaleString("en-US", {
      timeZone: "Asia/Hong_Kong",
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true
    });
  };

  const formatToLocalDateTime = (utcTimestamp) => {
    if (!utcTimestamp) return "-";
    const date = new Date(utcTimestamp);
    return date.toLocaleString("en-US", {
      timeZone: "Asia/Hong_Kong",
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true
    });
  };

  const formatDateOnly = (utcTimestamp) => {
    if (!utcTimestamp) return "-";
    const date = new Date(utcTimestamp);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric"
    });
  };

  const StatusBadge = ({ status }) => {
    const statusOptions = {
      expected: "bg-blue-100 text-blue-800 border border-blue-200",
      checked_in: "bg-green-100 text-green-800 border border-green-200",
      checked_out: "bg-gray-100 text-gray-800 border border-gray-200",
    };

    const statusLabels = {
      expected: "Expected",
      checked_in: "Checked-in",
      checked_out: "Checked-out",
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${statusOptions[status] || statusOptions.expected}`}>
        {statusLabels[status] || "Expected"}
      </span>
    );
  };

  const AgreementBadge = ({ agreement }) => {
    const getAgreementStyles = (agreement) => {
      switch (agreement?.toLowerCase()) {
        case "signed":
          return "bg-green-100 text-green-800";
        case "pending":
          return "bg-yellow-100 text-yellow-800";
        default:
          return "bg-gray-100 text-gray-800";
      }
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${getAgreementStyles(agreement)}`}>
        {agreement || "Pending"}
      </span>
    );
  };

  const ApprovalBadge = ({ approvalStatus }) => {
    const getApprovalStyles = (status) => {
      switch (status?.toLowerCase()) {
        case "approved":
          return "bg-green-100 text-green-800 border border-green-200";
        case "pending":
          return "bg-yellow-100 text-yellow-800 border border-yellow-200";
        case "rejected":
          return "bg-red-100 text-red-800 border border-red-200";
        default:
          return "bg-gray-100 text-gray-800 border border-gray-200";
      }
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${getApprovalStyles(approvalStatus)}`}>
        {approvalStatus || "Pending"}
      </span>
    );
  };

 

  const Modal = ({ show, message, type, onClose }) => {
    if (!show) return null;
  
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4 animate-fadeIn">
          <div className="flex items-center gap-3 mb-4">
            {type === "success" ? (
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            ) : (
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            )}
            <h3 className={`text-lg font-semibold ${type === "success" ? "text-green-800" : "text-red-800"}`}>
              {type === "success" ? "Success" : "Error"}
            </h3>
          </div>
          <p className="text-gray-700 mb-6">{message}</p>
          <button
            onClick={onClose}
            className={`w-full px-4 py-2 rounded-lg font-medium transition-colors ${
              type === "success"
                ? "bg-[#ce5841] hover:bg-orange-700 text-white"
                : "bg-red-700 hover:bg-red-800 text-white"
            }`}
          >
            Close
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Sidebar onLogout={handleLogout} />
        <div className="ml-32 mr-8 py-8 flex items-center justify-center">
          <p className="text-gray-500 text-sm">Loading visit details...</p>
        </div>
      </div>
    );
  }

  if (!visit) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Sidebar onLogout={handleLogout} />
        <div className="ml-32 mr-8 py-8">
          <button
            onClick={() => navigate("/log")}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="">Back to Logbook</span>
          </button>
          <p className="text-gray-500 text-sm">Visit not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar onLogout={handleLogout} />

      <div className="ml-32 mr-8 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate("/log")}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="font-medium">Back to Logbook</span>
        </button>

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">Visit Details</h1>
          <p className="text-gray-500 text-xs mt-1">Complete information about the visit</p>
        </div>

        {/* Two Column Layout */}
        <div className="flex gap-6">
          
          {/* Left Column - Main Details */}
          <div className="flex-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              
              {/* Visitor Information Section */}
              <div className="mb-6">
                <h2 className="text-base font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                  Visitor Information
                </h2>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <span className="text-xs text-gray-500 font-medium w-32">Name:</span>
                    <span className="text-sm text-gray-900">{visit.visitor || "-"}</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-xs text-gray-500 font-medium w-32">Company:</span>
                    <span className="text-sm text-gray-900">{visit.company || "-"}</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-xs text-gray-500 font-medium w-32">Type:</span>
                    <span className="text-sm text-gray-900">{visit.type || "-"}</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-xs text-gray-500 font-medium w-32">Purpose:</span>
                    <span className="text-sm text-gray-900">{visit.purpose || "-"}</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-xs text-gray-500 font-medium w-32">Vehicle Number:</span>
                    <span className="text-sm text-gray-900">{visit.vehicleNumber || "-"}</span>
                  </div>
                </div>
              </div>

              {/* Visit Details Section */}
              <div className="mb-6">
                <h2 className="text-base font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                  Visit Details
                </h2>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <span className="text-xs text-gray-500 font-medium w-32">Host Name:</span>
                    <span className="text-sm text-gray-900">{visit.hostname || "-"}</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-xs text-gray-500 font-medium w-32">Host Email:</span>
                    <span className="text-sm text-gray-900 break-all">{visit.host || "-"}</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-xs text-gray-500 font-medium w-32">Group:</span>
                    <span className="text-sm text-gray-900">{visit.group || "-"}</span>
                  </div>
                  <div className="flex items-start">
  <span className="text-xs text-gray-500 font-medium w-32">Visit Date:</span>
  <span className="text-sm text-gray-900">{formatDateOnly(visit.expectedTime || visit.timestamp)}</span>
</div>
                  <div className="flex items-start">
                    <span className="text-xs text-gray-500 font-medium w-32">Expected Time:</span>
                    <span className="text-sm text-gray-900">{formatExpectedTime(visit.expectedTime)}</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-xs text-gray-500 font-medium w-32">Agreement:</span>
                    <AgreementBadge agreement={visit.agreement} />
                  </div>
                </div>
              </div>

              {/* Status Information Section */}
              <div className="mb-6">
                <h2 className="text-base font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                  Status Information
                </h2>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <span className="text-xs text-gray-500 font-medium w-32">Visit Status:</span>
                    <StatusBadge status={visit.status} />
                  </div>
                  <div className="flex items-start">
                    <span className="text-xs text-gray-500 font-medium w-32">Check-in Time:</span>
                    <span className="text-sm text-gray-900">{formatToLocalTime(visit.checkInTime)}</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-xs text-gray-500 font-medium w-32">Check-out Time:</span>
                    <span className="text-sm text-gray-900">{formatToLocalTime(visit.checkOutTime)}</span>
                  </div>
                </div>
              </div>

           {/* Approval Section */}
<div>
  <h2 className="text-base font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
    Approval Details
  </h2>
  <div className="space-y-3">
    <div className="flex items-start">
      <span className="text-xs text-gray-500 font-medium w-32">Approval Status:</span>
      <ApprovalBadge approvalStatus={visit.approvalStatus} />
    </div>

    {visit.approvalStatus === "approved" ? (
      <>
        <div className="flex items-start">
          <span className="text-xs text-gray-500 font-medium w-32">Approved By:</span>
          <span className="text-sm text-gray-900">{visit.approvedBy || "-"}</span>
        </div>

        <div className="flex items-start"> 
          <span className="text-xs text-gray-500 font-medium w-32">Meeting Location:</span>
          <span className="text-sm text-gray-900">{visit.meetingLocation || "-"}</span>
        </div>
      </>
    ) : visit.approvalStatus === "rejected" ? (
      <div className="flex items-start">
        <span className="text-xs text-gray-500 font-medium w-32">Rejected By:</span>
        <span className="text-sm text-gray-900">{visit.rejectedBy || "-"}</span>
      </div>
    ) : (isAdmin || (userData?.email === visit.host)) && visit.agreement?.toLowerCase() === "signed" ? (
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex gap-3">
          <button
            onClick={handleApprove}
            disabled={isApproving || isRejecting}
            className="flex-1 px-4 py-2 text-sm bg-[#4ba059] text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
          >
            {isApproving ? "Approving..." : "Approve Visit"}
          </button>
          <button
            onClick={handleReject}
            disabled={isApproving || isRejecting}
            className="flex-1 px-4 py-2 text-sm bg-transparent text-red-600 border-2 border-red-600 rounded-lg hover:bg-red-700 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
          >
            {isRejecting ? "Rejecting..." : "Reject Visit"}
          </button>
        </div>
      </div>
    ) : (
      <div className="flex items-start">
        <span className="text-xs text-gray-500 font-medium w-32">Status:</span>
        <span className="text-sm text-gray-600 italic">
          {visit.agreement?.toLowerCase() === "signed" 
            ? "Waiting for approval..." 
            : "Waiting for visitor to sign agreement..."}
        </span>
      </div>
    )}
  </div>
</div>
            </div>
          </div>

         {/* Right Column - Timeline */}
<div className="w-80">
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
    <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
      <Clock className="w-4 h-4" />
      Event Timeline
    </h2>
    
    <div className="space-y-4">
      {/* Invite Created */}
      <div className="flex gap-3">
        <div className="flex flex-col items-center">
          <div className="w-2 h-2 rounded-full bg-[#ce5841] mt-1.5"></div>
          <div className="w-0.5 h-full bg-gray-200 mt-1"></div>
        </div>
        <div className="flex-1 pb-4">
          <p className="text-sm font-medium text-gray-900">Invite Created</p>
          <p className="text-xs text-gray-600 mt-1">{formatToHKT(visit.timestamp)}</p>
        </div>
      </div>

      {/* Agreement Signed */}
      {visit.signedAt && (
        <div className="flex gap-3">
          <div className="flex flex-col items-center">
            <div className="w-2 h-2 rounded-full bg-green-500 mt-1.5"></div>
            <div className="w-0.5 h-full bg-gray-200 mt-1"></div>
          </div>
          <div className="flex-1 pb-4">
            <p className="text-sm font-medium text-gray-900">Agreement Signed</p>
            <p className="text-xs text-gray-600 mt-1">{formatToHKT(visit.signedAt)}</p>
          </div>
        </div>
      )}

      {/* Approval */}
      {visit.approvalStatus === "approved" && visit.approvedAt && (
        <div className="flex gap-3">
          <div className="flex flex-col items-center">
            <div className="w-2 h-2 rounded-full bg-green-500 mt-1.5"></div>
            <div className="w-0.5 h-full bg-gray-200 mt-1"></div>
          </div>
          <div className="flex-1 pb-4">
            <p className="text-sm font-medium text-gray-900">Visit Approved</p>
            <p className="text-xs text-gray-600 mt-1">By {visit.approvedBy}</p>
            <p className="text-xs text-gray-600">{formatToLocalDateTime(visit.approvedAt)}</p>
          </div>
        </div>
      )}

      {/* Check-in */}
      {visit.checkInTime && (
        <div className="flex gap-3">
          <div className="flex flex-col items-center">
            <div className="w-2 h-2 rounded-full bg-green-500 mt-1.5"></div>
            <div className="w-0.5 h-full bg-gray-200 mt-1"></div>
          </div>
          <div className="flex-1 pb-4">
            <p className="text-sm font-medium text-gray-900">Checked In</p>
            <p className="text-xs text-gray-600 mt-1">{formatToLocalDateTime(visit.checkInTime)}</p>
          </div>
        </div>
      )}

      {/* Check-out */}
      {visit.checkOutTime && (
        <div className="flex gap-3">
          <div className="flex flex-col items-center">
            <div className="w-2 h-2 rounded-full bg-gray-500 mt-1.5"></div>
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">Checked Out</p>
            <p className="text-xs text-gray-600 mt-1">{formatToLocalDateTime(visit.checkOutTime)}</p>
          </div>
        </div>
      )}

      {/* Pending status */}
      {!visit.checkInTime && visit.approvalStatus === "pending" && (
        <div className="flex gap-3">
          <div className="flex flex-col items-center">
            <div className="w-2 h-2 rounded-full bg-yellow-500 mt-1.5"></div>
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">Awaiting Approval</p>
            <p className="text-xs text-gray-600 mt-1">Pending admin approval</p>
          </div>
        </div>
      )}
    </div>
  </div>
</div>

        </div>
      </div>
      <Modal 
      show={showModal} 
      message={modalMessage} 
      type={modalType}
      onClose={() => setShowModal(false)}
    />
    </div>
  );
};

export default VisitDetails;